/** What each added activity still needs before it can start, worst first (`SES-021`). */

import { Button, Stack, StatusBadge, Toolbar } from "@/components/ui";
import type { StatusTone } from "@/components/ui";

import { useCanvasPairings } from "../../account/lib/queries";
import { Requirements } from "../../components/Requirements";
import { Muted } from "../../components/Text";
import { useActivityVerb } from "../../spaces/lib/queries";
import type { SpaceActivities } from "../../spaces/lib/queries";
import type { AddedActivityRow, NamedCanvas, SpaceRow } from "../../types";
import { isLive, runStatusOf } from "../../lib/runState";
import { useWires } from "../lib/wires";

import {
  activityChip,
  nothingDrivesIt,
  spaceReadiness,
  startable,
  unpairedCanvases,
  versionPending,
  type ActivityReadiness,
  type ReadinessState,
} from "../lib/readiness";

import { Nothing, Section } from "./Section";
import type { PanelId } from "./panels";

const TONES: Record<ReadinessState, StatusTone> = {
  ready: "success",
  unwired: "warning",
  ambiguous: "warning",
  unavailable: "danger",
  optional_unwired: "neutral",
};

function Readiness({
  readiness,
  activities,
  spaceId,
  undriven,
  unpaired,
  onOpenPanel,
}: {
  readonly readiness: ActivityReadiness;
  readonly activities: SpaceActivities;
  readonly spaceId: string;
  readonly undriven: boolean;
  readonly unpaired: readonly NamedCanvas[];
  readonly onOpenPanel: (id: PanelId) => void;
}) {
  const verb = useActivityVerb();
  const row: AddedActivityRow = readiness.row;
  const entry = activities.entryFor(row);
  const live = isLive(runStatusOf(row.mount));
  const may = startable(readiness);
  const holding = readiness.requirements.find((one) => one.state === readiness.state);
  const chip = activityChip(readiness, undriven, unpaired.length > 0, versionPending(row) !== undefined);

  return (
    <Section
      title={row.name}
      action={<StatusBadge tone={chip.tone}>{chip.label}</StatusBadge>}
    >
      <Stack gap={3}>
        {unpaired.length === 0 ? null : (
          <div className="cx-brings">
            <span className="cx-key">Canvas</span>
            <Muted small>{unpaired.map((one) => one.kind).join(", ")}</Muted>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                onOpenPanel("canvases");
              }}
            >
              Pair
            </Button>
          </div>
        )}

        {readiness.underived ? (
          <Muted small>No manifest for this version</Muted>
        ) : readiness.requirements.length === 0 ? (
          <Muted small>Requires nothing</Muted>
        ) : (
          <Stack gap={2}>
            {readiness.requirements.map((one) => (
              <div className="cx-row" key={one.requirement.name}>
                <div className="cx-row-id">
                  <div>
                    <div className="cx-node-name">{one.requirement.name}</div>
                    <Muted small>
                      {`${one.requirement.type}, ${String(one.filled)} of ${String(one.available)} filled`}
                    </Muted>
                    {one.remedy === "" ? null : <Muted small>{one.remedy}</Muted>}
                  </div>
                </div>
                <StatusBadge tone={TONES[one.state]}>{one.state}</StatusBadge>
              </div>
            ))}
          </Stack>
        )}

        {live ? null : (
          <Toolbar>
            <Button
              size="sm"
              variant="primary"
              disabled={!may}
              loading={verb.isPending}
              onClick={() => {
                verb.mutate({ row, verb: "Start" });
              }}
            >
              Start
            </Button>
            {may ? null : (
              <Muted small>{holding === undefined ? "" : holding.remedy}</Muted>
            )}
          </Toolbar>
        )}

        {entry === undefined || readiness.requirements.length === 0 ? null : (
          <Requirements added={row} entry={entry} spaceId={spaceId} />
        )}
      </Stack>
    </Section>
  );
}

export interface NeedsPanelProps {
  readonly space: SpaceRow;
  readonly activities: SpaceActivities;
  /** One activity when one is selected, otherwise every one of them. */
  readonly selected: AddedActivityRow | null;
  readonly onOpenPanel: (id: PanelId) => void;
}

export function NeedsPanel({ space, activities, selected, onOpenPanel }: NeedsPanelProps) {
  const spaceId = space.space.space_id;
  const rows = selected === null ? activities.rows : [selected];
  const readings = spaceReadiness(rows, activities, space.connections);
  const wires = useWires(spaceId);
  const wireRows = wires.data ?? [];
  const pairings = useCanvasPairings();
  const pairingRows = pairings.data ?? [];

  if (activities.pending || activities.manifestsPending) {
    return <Nothing headline="Reading what is added" />;
  }
  if (readings.length === 0) return <Nothing headline="Nothing added" />;

  return (
    <Stack gap={5}>
      {readings.map((one) => (
        <Readiness
          key={one.row.added_activity_id}
          readiness={one}
          activities={activities}
          spaceId={spaceId}
          undriven={!wires.isPending && nothingDrivesIt(one.row, activities, wireRows)}
          unpaired={pairings.isPending ? [] : unpairedCanvases(one.row, activities, pairingRows)}
          onOpenPanel={onOpenPanel}
        />
      ))}
    </Stack>
  );
}
